import React from 'react';
import PropTypes from 'prop-types';

import Button from '../../library/button/button';
import Typography from '../../library/typography/typography';
import MinimumPriceMessage from '../../components/minimum-price-message/minimum-price-message';
import StackedMarkdownPrice from '../../components/stacked-markdown-price/stacked-markdown-price';
import InstallationCostsMessage from '../installation-costs-message/installation-costs-message';
import Price from '../price/price';
import ProductDescription from '../product-description/product-description';
import ProductImage from '../product-image/product-image';
import ProductOECompatible from '../product-oe-compatible/product-oe-compatible';
import ProductSwatchDescription from '../product-swatch-description/product-swatch-description';
import QuantityDisclaimer from '../quantity-disclaimer/quantity-disclaimer';
import StaticQuantity from '../static-quantity/static-quantity';
import ViewOnVehicleButton from '../view-on-vehicle-button/view-on-vehicle-button';

import {
  useMyAccount,
  useProductEntryDataLayerValues
} from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import {
  getMapEntryInstantSavingsInfo,
  getShouldShowEntryMarkdownPrice
} from '../../shared/utils/cart/cart';
import {
  getNormalizedProductType,
  getPriceInfo,
  getShouldShowMinimumPriceMessage
} from '../../shared/utils/product/product';
import { ProductProp } from '../../shared/utils/prop-types/prop-types';

import { PRODUCT_TYPES } from '../../shared/constants/products';

import './bundle-item-product-description.scss';

function BundleItemProductDescription(props) {
  const {
    hideBrand = false,
    hideGbb = false,
    hideName = false,
    hideLogo = false,
    hideQty = false,
    showProductCode = false,
    showViewOnVehicle = false,
    isPriceForEach = false,
    entry,
    link,
    hideChangeProduct,
    hideImage = false,
    isPrebuilt,
    isPackage,
    isStaggered,
    onChangeProduct,
    shouldDisplayTotalPrice,
    showInstallationCostsMessage = false,
    showMinimumPriceMessage: showMinimumPriceMessageProp = false,
    showQuantityDisclaimer = false,
    shouldShowCartPrice = false,
    hideLabel = false,
    axleType,
    totalQuantity: totalQuantityProp
  } = props;

  const {
    product,
    quantity,
    mapPrice: entryMapPrice,
    totalQuantity = totalQuantityProp || quantity
  } = entry || {};
  const { isLoggedIn } = useMyAccount();

  const { value: strikethroughPromotionPrice = 0 } =
    entry?.orderEntryStrikethrough?.perUnitPrice || {};
  const { vendorFinish } = product || {};

  const { price: productPrice, mapPrice: productMapPrice } =
    getPriceInfo(product);

  const price = shouldShowCartPrice
    ? entryMapPrice?.value || productPrice
    : productPrice;
  const mapPrice = shouldShowCartPrice
    ? {}
    : {
        ...productMapPrice,
        price:
          process.env.__FF_MAP_PRICE_STRIKE_THROUGH__ === 'true' &&
          strikethroughPromotionPrice
            ? strikethroughPromotionPrice
            : productMapPrice?.price *
              (isPriceForEach && !shouldDisplayTotalPrice ? 1 : quantity)
      };

  const { instantMapSavingsPerProduct = 0 } = getMapEntryInstantSavingsInfo(
    entry,
    isLoggedIn,
    totalQuantity
  );
  const mapShowMinimumPriceMessage =
    showMinimumPriceMessageProp &&
    process.env.__FF_MAP_PRICE_STRIKE_THROUGH__ === 'true' &&
    instantMapSavingsPerProduct
      ? !isLoggedIn
      : showMinimumPriceMessageProp;

  const showMinimumPriceMessage =
    mapShowMinimumPriceMessage && getShouldShowMinimumPriceMessage(product);
  const productType = getNormalizedProductType(product);
  const logoUrl = entry?.product?.brandLogo?.url;

  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const showMarkdownPrice = getShouldShowEntryMarkdownPrice(
    entry,
    totalQuantity
  );

  return (
    <div
      className="bundle-item-product-description"
      styleName={`container ${
        isStaggered || !isPackage || product?.isOtherVehiclesOE
          ? 'container--without-border'
          : ''
      }`}
    >
      {!hideLabel && (
        <Typography
          className="bundle-item-product-description__type"
          styleName="type"
          tag="div"
          variant="H5"
          weight="black"
        >
          {productType}
        </Typography>
      )}
      <div styleName="image-product-container">
        {!hideImage && (
          <ProductImage
            height={114}
            product={entry?.product}
            styleName="image"
            width={114}
          />
        )}
        <div
          className="bundle-item-product-description__product-container"
          styleName="product-container"
        >
          <div styleName="description-section">
            <div>
              <div>
                {isStaggered && (
                  <Typography tag="span" weight="black">
                    {axleType}
                  </Typography>
                )}
                <ProductDescription
                  brand={!hideBrand && product?.brand}
                  brandLogoUrl={!hideLogo && logoUrl}
                  code={showProductCode && product?.code}
                  gbb={!hideGbb && product?.gbb}
                  link={link}
                  name={!hideName && product?.name}
                  showGBBTooltip={!hideGbb}
                  size={product?.size}
                />
                {showViewOnVehicle &&
                  isPrebuilt &&
                  productType === PRODUCT_TYPES.WHEELS && (
                    <div styleName="vov">
                      <ViewOnVehicleButton
                        detailUrl={link}
                        hideAddProduct
                        onClick={() =>
                          trackAction(
                            'product_list_view_on_vehicle',
                            getProductEntryDataLayerValues(product)
                          )
                        }
                        product={product}
                        shouldSkipQuery
                      />
                    </div>
                  )}
              </div>
              {showViewOnVehicle && !isPrebuilt && (
                <ViewOnVehicleButton
                  detailUrl={link}
                  hideAddProduct
                  onClick={() =>
                    trackAction(
                      'product_list_view_on_vehicle',
                      getProductEntryDataLayerValues(product)
                    )
                  }
                  product={product}
                  shouldSkipQuery
                />
              )}
              {vendorFinish &&
                productType === PRODUCT_TYPES.WHEELS &&
                !isStaggered && (
                  <div styleName="swatch-description">
                    <ProductSwatchDescription finish={vendorFinish} />
                  </div>
                )}

              <div
                className="bundle-item-product-description__price-qty-container"
                styleName="price-qty-container"
              >
                {price && (
                  <div styleName="price-container">
                    {showMarkdownPrice && (
                      <StackedMarkdownPrice
                        entry={entry}
                        totalQuantity={totalQuantity}
                      />
                    )}
                    <Price
                      isPriceForEach={isPriceForEach}
                      mapPrice={mapPrice}
                      price={price * (isPriceForEach ? 1 : quantity)}
                      unitName={product?.unitName || 'EA'}
                      variant="H3"
                    />
                    {showMinimumPriceMessage && (
                      <MinimumPriceMessage
                        message={product?.mapDisplayRule?.message}
                      />
                    )}
                    {showInstallationCostsMessage && (
                      <InstallationCostsMessage />
                    )}
                  </div>
                )}
                {!isStaggered && !hideQty && (
                  <StaticQuantity isHorizontal quantity={quantity} />
                )}
              </div>
            </div>
            {isStaggered && !hideQty && (
              <StaticQuantity
                quantity={quantity}
                styleName="staggered-quantity"
              />
            )}
          </div>
          {showQuantityDisclaimer && (
            <QuantityDisclaimer
              productType={productType}
              quantity={quantity}
              styleName="quantity-disclaimer"
            />
          )}

          <div
            className="bundle-item-product-description__ctas"
            styleName="ctas"
          >
            {onChangeProduct && !hideChangeProduct && (
              <Button onClick={onChangeProduct}>
                change {productType?.toLowerCase()}
              </Button>
            )}
          </div>
        </div>
      </div>
      {!isStaggered && product?.isOtherVehiclesOE && (
        <ProductOECompatible styleName="product-oe-compatible" />
      )}
    </div>
  );
}

BundleItemProductDescription.propTypes = {
  axleType: PropTypes.string,
  entry: PropTypes.shape({
    entryNumber: PropTypes.number,
    orderEntryStrikethrough: PropTypes.object,
    product: ProductProp,
    quantity: PropTypes.number
  }),
  hideBrand: PropTypes.bool,
  hideChangeProduct: PropTypes.bool,
  hideGbb: PropTypes.bool,
  hideImage: PropTypes.bool,
  hideLabel: PropTypes.bool,
  hideLogo: PropTypes.bool,
  hideName: PropTypes.bool,
  hideQty: PropTypes.bool,
  isPackage: PropTypes.bool,
  isPrebuilt: PropTypes.bool,
  isPriceForEach: PropTypes.bool,
  isStaggered: PropTypes.bool,
  link: PropTypes.string,
  onChangeProduct: PropTypes.func,
  shouldDisplayTotalPrice: PropTypes.bool,
  shouldShowCartPrice: PropTypes.bool,
  showInstallationCostsMessage: PropTypes.bool,
  showMinimumPriceMessage: PropTypes.bool,
  showProductCode: PropTypes.bool,
  showQuantityDisclaimer: PropTypes.bool,
  showViewOnVehicle: PropTypes.bool,
  totalQuantity: PropTypes.number
};

export default BundleItemProductDescription;
